import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Eye, EyeOff, Trash2, Send, Check, X, Download, Upload, Languages } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function AdminInstruments() {
  const [list, setList] = useState([]);
  const { user } = useAuth();
  const { lang, pick } = useLang();
  const nav = useNavigate();
  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const isReviewer = role === "reviewer";
  const fileRef = useRef(null);
  const textFileRef = useRef(null);
  const [textTarget, setTextTarget] = useState(null);

  const [originFilter, setOriginFilter] = useState("all");
  const load = () => api.get("/admin/instruments").then(r => setList(r.data));
  const originCount = (o) => list.filter((i) => (i.origin || "") === o).length;
  const shown = originFilter === "all" ? list : list.filter((i) => (i.origin || "") === originFilter);
  useEffect(() => { load(); }, []);

  const togglePublish = async (id) => { try { const { data } = await api.post(`/admin/instruments/${id}/publish`); toast.success(data.status === "published" ? "Published" : "Unpublished"); load(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } };
  const remove = async (id) => { try { await api.delete(`/admin/instruments/${id}`); toast.success(lang === "id" ? "Dipindahkan ke Sampah" : "Moved to Trash"); load(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } };
  const submitReview = async (id) => { await api.post(`/admin/instruments/${id}/submit-review`, { note: "" }); toast.success("Submitted"); load(); };
  const approve = async (id) => { await api.post(`/admin/instruments/${id}/approve`, { note: "" }); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/instruments/${id}/reject`, { note: "" }); toast.success("Rejected"); load(); };

  const doExport = (id, slug, format) => {
    const token = localStorage.getItem("se_token");
    fetch(`${API_BASE}/admin/instruments/${id}/export?format=${format}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${slug}.${format}`;
        a.click();
      });
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/instruments/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.slug} (${data.questions_imported} questions)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    e.target.value = "";
  };

  const pickTextFile = (inst) => { setTextTarget(inst); textFileRef.current?.click(); };

  // Updates only the ID/EN wording of an existing instrument; its responses, scoring and structure stay.
  const doImportText = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !textTarget) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/instruments/${textTarget.id}/import-text`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.fields_updated
        ? (lang === "id" ? `Teks diperbarui: ${data.fields_updated} kolom` : `Text updated: ${data.fields_updated} fields`)
        : (lang === "id" ? "Tidak ada teks yang berubah" : "No text changed"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setTextTarget(null);
  };

  const statusBadge = (status) => {
    const map = {
      draft: { text: "Draft", cls: "border-border" },
      pending_review: { text: lang === "id" ? "Menunggu Review" : "Pending Review", cls: "border-terracotta-300 bg-terracotta-50 text-terracotta-700" },
      published: { text: "Published", cls: "border-sage-300 bg-sage-50 text-teal-800" },
    };
    const s = map[status] || map.draft;
    return <Badge variant="outline" className={s.cls}>{s.text}</Badge>;
  };

  // Mindovia Original = items written by Mindovia (may be monetized); published = someone else's scale.
  const originBadge = (i) => {
    const map = {
      original: { text: "Mindovia Original", cls: "border-sage-300 bg-sage-50 text-teal-800" },
      published: { text: lang === "id" ? "Skala terbit" : "Published scale", cls: "border-terracotta-300 bg-terracotta-50 text-terracotta-700" },
    };
    const s = map[i.origin];
    const hint = !i.origin_set && i.origin ? (lang === "id" ? "Ditebak dari catatan sumber. Tandai di tab Hasil." : "Guessed from the source note. Set it in the Results tab.") : undefined;
    if (!s) return <Badge variant="outline" className="border-dashed text-muted-foreground" title={lang === "id" ? "Tandai di tab Hasil → Monetisasi" : "Set it in Results → Monetization"}>{lang === "id" ? "Belum ditandai" : "Not set"}</Badge>;
    return (
      <span className="inline-flex items-center gap-1 flex-wrap" title={hint}>
        <Badge variant="outline" className={`${s.cls}${i.origin_set ? "" : " border-dashed"}`} data-testid={`admin-origin-${i.slug}`}>{s.text}{i.origin_set ? "" : " ?"}</Badge>
        {i.paid && <Badge variant="outline" className={i.origin === "original" ? "border-border" : "border-destructive text-destructive"}>{lang === "id" ? "Berbayar" : "Paid"}</Badge>}
      </span>
    );
  };

  const originFilters = [
    { value: "all", text: lang === "id" ? "Semua" : "All", count: list.length },
    { value: "original", text: "Mindovia Original", count: originCount("original") },
    { value: "published", text: lang === "id" ? "Skala terbit" : "Published scale", count: originCount("published") },
    { value: "", text: lang === "id" ? "Belum ditandai" : "Not set", count: originCount("") },
  ];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "Instrumen" : "Instruments"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{lang === "id" ? "Kelola kuesioner, dimensi, dan skoring." : "Manage questionnaires, dimensions, and scoring."}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".json,.xlsx" onChange={doImport} className="hidden" data-testid="import-file-input" />
          <input ref={textFileRef} type="file" accept=".json" onChange={doImportText} className="hidden" data-testid="import-text-file-input" />
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="admin-import-btn"><Upload className="w-4 h-4 mr-1" /> {lang === "id" ? "Impor" : "Import"}</Button>
          )}
          <Button data-testid="admin-new-instrument-btn" onClick={() => nav("/admin/instruments/new")} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
            <Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Instrumen Baru" : "New Instrument"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-origin-filter">
        {originFilters.map((f) => (
          <Button key={f.value || "unset"} size="sm" variant={originFilter === f.value ? "default" : "outline"} className="rounded-full"
            onClick={() => setOriginFilter(f.value)} data-testid={`admin-origin-filter-${f.value || "unset"}`}>
            {f.text} <span className="ml-1 opacity-70">{f.count}</span>
          </Button>
        ))}
      </div>

      <div className="rounded-2xl border border-border overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left p-4">{lang === "id" ? "Nama" : "Name"}</th>
              <th className="text-left p-4">{lang === "id" ? "Kategori" : "Category"}</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">{lang === "id" ? "Asal item" : "Item origin"}</th>
              <th className="text-left p-4">{lang === "id" ? "Pertanyaan" : "Questions"}</th>
              <th className="text-right p-4">{lang === "id" ? "Aksi" : "Actions"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {shown.map((i) => (
              <tr key={i.id} data-testid={`admin-instrument-row-${i.slug}`}>
                <td className="p-4">
                  <div className="font-medium">{pick(i, "name")}</div>
                  <div className="text-xs text-muted-foreground font-mono">{i.slug}</div>
                </td>
                <td className="p-4 text-muted-foreground">{i.category ? pick(i.category, "name") : "-"}</td>
                <td className="p-4">{statusBadge(i.status)}</td>
                <td className="p-4">{originBadge(i)}</td>
                <td className="p-4">{i.question_count}</td>
                <td className="p-4 text-right space-x-1">
                  {i.status === "draft" && (
                    <Button data-testid={`admin-submit-review-${i.slug}`} size="icon" variant="ghost" onClick={() => submitReview(i.id)} title="Submit for review"><Send className="w-4 h-4" /></Button>
                  )}
                  {i.status === "pending_review" && (isAdmin || isReviewer) && (
                    <>
                      <Button data-testid={`admin-approve-${i.slug}`} size="icon" variant="ghost" onClick={() => approve(i.id)} className="text-sage-500" title="Approve"><Check className="w-4 h-4" /></Button>
                      <Button data-testid={`admin-reject-${i.slug}`} size="icon" variant="ghost" onClick={() => reject(i.id)} className="text-destructive" title="Reject"><X className="w-4 h-4" /></Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button data-testid={`admin-toggle-publish-${i.slug}`} size="icon" variant="ghost" onClick={() => togglePublish(i.id)} title="Toggle publish">
                      {i.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  )}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="icon" variant="ghost" title="Export" data-testid={`admin-export-${i.slug}`}><Download className="w-4 h-4" /></Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-40 p-1">
                      {["json","xlsx","pdf","csv"].map(f => (
                        <button key={f} onClick={() => doExport(i.id, i.slug, f)} data-testid={`admin-export-${i.slug}-${f}`}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary rounded uppercase">{f}</button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  {isAdmin && (
                    <Button size="icon" variant="ghost" onClick={() => pickTextFile(i)} data-testid={`admin-import-text-${i.slug}`}
                      title={lang === "id" ? "Perbarui teks ID/EN dari JSON" : "Update ID/EN text from JSON"}><Languages className="w-4 h-4" /></Button>
                  )}
                  <Link to={`/admin/instruments/${i.id}/edit`}>
                    <Button data-testid={`admin-edit-${i.slug}`} size="icon" variant="ghost"><Pencil className="w-4 h-4" /></Button>
                  </Link>
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`admin-delete-${i.slug}`} size="icon" variant="ghost" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{lang === "id" ? "Hapus instrumen?" : "Delete instrument?"}</AlertDialogTitle>
                          <AlertDialogDescription>{lang === "id" ? "Instrumen akan dipindahkan ke Sampah dan dapat dipulihkan." : "The instrument will move to Trash and can be restored."}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{lang === "id" ? "Batal" : "Cancel"}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(i.id)} data-testid={`admin-confirm-delete-${i.slug}`}>{lang === "id" ? "Hapus" : "Delete"}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (<tr><td colSpan={6} className="p-10 text-center text-muted-foreground">{lang === "id" ? "Belum ada instrumen." : "No instruments yet."}</td></tr>)}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
